/**
 * AI-label disputes — the human_review queue (design/MEDIA_EMBEDDINGS_PLAN.md §12.4).
 *
 * Every case here is one where the author says the picture is a camera photo
 * and the detector said otherwise — and, until G14 is deployed, NO model has
 * given a second opinion. A person decides:
 *
 *   Uphold    the label returns. The author chose "a camera photo I edited"?
 *             The Terms say heavy editing can produce the label (ToS §5);
 *             upholding is not calling them a liar, and the author-facing copy
 *             says so.
 *   Overturn  the label stays off for good and the case becomes hard-negative
 *             training data.
 *
 * Two-step confirm on both (§0c — no window.confirm). The evidence is the
 * picture, the raw score against the threshold, the author's answer, and —
 * when the author disclosed elsewhere — that fact. A strike for a fraudulent
 * pattern is deliberately a separate, explicit action here, never automatic.
 */

import { parseServerDate } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { moderationApi, type AiDispute } from '../lib/moderationApi';
import { EvidenceLightbox, type EvidenceItem } from '../components/EvidenceLightbox';
import { TabBar } from '../components/ui/TabBar';
import { PageShell } from '../components/ui/PageShell';

export const Route = createFileRoute('/ai-disputes')({
  component: AiDisputesRoute,
});

function AiDisputesRoute() {
  return (
    <AdminShell mode="feature" featureName="AI-label disputes">
      <DisputesConsole />
    </AdminShell>
  );
}

type View = 'human_review' | 'decided';

function DisputesConsole() {
  const [view, setView] = useState<View>('human_review');
  const [items, setItems] = useState<AiDispute[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [arming, setArming] = useState<{ id: string; action: 'uphold' | 'overturn' } | null>(null);
  // Adjudicating an AI label means LOOKING at the picture. Opening each one in
  // a new tab — what this route did — is the exact workflow EvidenceLightbox
  // was written to kill on the verification queue (founder 2026-08-06).
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setItems(null);
    setError(null);
    moderationApi
      .aiDisputes(view)
      .then((r) => setItems(r.disputes))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'failed'));
  }, [view]);
  useEffect(load, [load]);

  async function decide(id: string, action: 'uphold' | 'overturn') {
    if (arming?.id !== id || arming.action !== action) {
      setArming({ id, action });
      return;
    }
    setBusy(id);
    try {
      await moderationApi.aiDisputeDecide(id, action);
      setItems((cur) => (cur ? cur.filter((d) => d.id !== id) : cur));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'failed');
    } finally {
      setBusy(null);
      setArming(null);
    }
  }

  return (
    <PageShell
      title="AI label disputes"
      description="Pictures an author says were wrongly labelled as AI-generated. Overturning clears the label; upholding leaves it in place."
      maxWidth={1100}
    >
      <TabBar
        label="AI dispute queue"
        activeKey={view}
        onSelect={setView}
        items={[
          { key: 'human_review', label: 'Needs a decision' },
          { key: 'decided', label: 'Decided' },
        ]}
      />

      {error ? (
        <p role="alert" className="mt-3 text-[13px]" style={{ color: 'var(--c-state-danger, #C7563E)' }}>
          {error}
        </p>
      ) : null}

      {/* A failed load shows its error, not an endless "Loading…" (Q audit 2026-09-23). */}
      {error && items === null ? null : items === null ? (
        <p className="mt-4 text-[13px]" style={{ color: 'var(--c-fg-tertiary)' }}>Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-4 text-[13px]" style={{ color: 'var(--c-fg-tertiary)' }}>
          {view === 'human_review' ? 'Nothing waiting for a decision.' : 'No decided cases yet.'}
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {items.map((d) => (
            <li key={d.id} className="flex gap-4 rounded-soft border border-hairline bg-surface p-3">
              {/* Still an <a> with a real href, so middle-click and cmd-click
                  keep working for a reviewer who genuinely wants a tab; the
                  plain click is intercepted and opens the viewer instead.
                  Same pattern verification.tsx uses. */}
              <a
                href={d.url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${d.kind === 'avatar' ? 'profile picture' : 'post picture'} from @${d.handle}`}
                onClick={(e) => {
                  if (!d.url || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                  e.preventDefault();
                  setLightbox(items.findIndex((x) => x.id === d.id));
                }}
                className="shrink-0"
                style={{
                  width: 160,
                  height: 160,
                  background: 'var(--c-bg-surface-alt)',
                  borderRadius: d.kind === 'avatar' ? 80 : 6,
                  overflow: 'hidden',
                  display: 'block',
                  cursor: d.url ? 'zoom-in' : 'default',
                }}
              >
                {d.url ? <img src={d.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : null}
              </a>
              <div className="min-w-0 flex-1 text-[13px]" style={{ color: 'var(--c-fg-default)' }}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-semibold">@{d.handle}</span>
                  <span style={{ color: 'var(--c-fg-tertiary)' }}>{d.kind === 'avatar' ? 'profile picture' : 'post picture'}</span>
                  {d.post_url ? (
                    <a href={d.post_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--c-fg-link)' }}>
                      open post
                    </a>
                  ) : null}
                  <span style={{ color: 'var(--c-fg-tertiary)' }}>{parseServerDate(d.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-1.5" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  score <b>{d.detect_score.toFixed(2)}</b> vs threshold {d.threshold.toFixed(2)} · head {d.head_version} · second opinion{' '}
                  <b>{d.second_opinion}</b>
                  {d.second_model ? ` (${d.second_model}${typeof d.second_score === 'number' ? `, confidence ${d.second_score.toFixed(2)}` : ''})` : null}
                  {d.state === 'open' ? ' · still open — the box worker has not answered; decide it yourself' : null}
                </div>
                {d.second_reason ? (
                  // The judge's own words, labelled as the MODEL's — never to be
                  // confused with what the author typed (the block below).
                  <div className="mt-1.5" style={{ color: 'var(--c-fg-secondary)' }}>
                    [second opinion] {d.second_reason}
                  </div>
                ) : null}
                <div className="mt-1.5">
                  Author says: <b>{d.reason === 'camera_unedited' ? 'a camera photo, barely edited' : d.reason === 'camera_edited' ? 'a camera photo I edited' : d.reason}</b>
                  {d.note ? <span style={{ color: 'var(--c-fg-secondary)' }}> — “{d.note}”</span> : null}
                </div>
                {d.reason === 'camera_edited' ? (
                  <p className="mt-1 text-[12px]" style={{ color: 'var(--c-fg-secondary)' }}>
                    Heavy editing can produce the label by design (ToS §5). Upholding does not say the author lied.
                  </p>
                ) : null}
                {d.state !== 'human_review' && d.state !== 'open' ? (
                  <div className="mt-1.5 text-[12px]" style={{ color: 'var(--c-fg-secondary)' }}>
                    <b style={{ color: d.state === 'upheld' ? '#9A4230' : '#5C7A3D' }}>{d.state}</b> by {d.decided_by ?? '—'}
                    {d.decided_at ? ` · ${parseServerDate(d.decided_at).toLocaleString()}` : ''}
                  </div>
                ) : (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      disabled={busy === d.id}
                      onClick={() => decide(d.id, 'overturn')}
                      className="rounded-full px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50"
                      style={{ border: '1px solid var(--c-border-strong)', background: arming?.id === d.id && arming.action === 'overturn' ? '#5C7A3D' : 'var(--c-bg-surface)', color: arming?.id === d.id && arming.action === 'overturn' ? '#FAF7F2' : 'var(--c-fg-default)' }}
                    >
                      {arming?.id === d.id && arming.action === 'overturn' ? 'Click again to remove the label' : 'Overturn — remove label'}
                    </button>
                    <button
                      type="button"
                      disabled={busy === d.id}
                      onClick={() => decide(d.id, 'uphold')}
                      className="rounded-full px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50"
                      style={{ border: '1px solid var(--c-border-strong)', background: arming?.id === d.id && arming.action === 'uphold' ? '#9A4230' : 'var(--c-bg-surface)', color: arming?.id === d.id && arming.action === 'uphold' ? '#FAF7F2' : 'var(--c-fg-default)' }}
                    >
                      {arming?.id === d.id && arming.action === 'uphold' ? 'Click again to keep the label' : 'Uphold — keep label'}
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Every disputed picture in the queue becomes one navigable set, so a
          reviewer can page through them with the arrow keys instead of
          opening and closing tabs. */}
      {lightbox !== null && items ? (
        <EvidenceLightbox
          items={items
            .filter((d): d is AiDispute & { url: string } => Boolean(d.url))
            .map<EvidenceItem>((d) => ({
              url: d.url,
              label: `@${d.handle} — ${d.kind === 'avatar' ? 'profile picture' : 'post picture'}`,
              isImage: true,
            }))}
          index={Math.max(0, Math.min(lightbox, items.filter((d) => d.url).length - 1))}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </PageShell>
  );
}
